/* global runtime */
// Advisor goals, planned events and backend insights; classic script loaded before backend.jsx.
const ADVISOR_GOAL_METRICS = ['buffer', 'income', 'result', 'margin'];
const ADVISOR_EVENT_KINDS = ['salary', 'tax', 'supplier', 'investment', 'loan', 'other'];
const normalizeAdvisorGoal = (goal = {}) => {
  const metric = ADVISOR_GOAL_METRICS.includes(goal.metric) ? goal.metric : 'buffer';
  return stripUndefinedDeep({
    id: String(goal.id || createId('goal')),
    title: String(goal.title || '').trim().slice(0, 160),
    metric,
    target: num(goal.target, 0),
    deadline: String(goal.deadline || '').slice(0, 10),
    startYear: Number(goal.startYear) || Number(String(goal.createdAt || '').slice(0, 4)) || new Date().getFullYear(),
    completedAt: goal.completedAt ? String(goal.completedAt).slice(0, 24) : null,
    createdAt: goal.createdAt || nowIso(),
    updatedAt: nowIso(),
  });
};
// Beløpet er fortegnssatt: negativt tapper kontoen, positivt fyller den. Det er
// brukerens eget tall, og det er dette som gjør kontantstrømkurven til en faktisk
// prognose i stedet for en flat linje etter dagens måned.
const normalizeAdvisorEvent = (event = {}) => stripUndefinedDeep({
  id: String(event.id || createId('event')),
  label: String(event.label || '').trim().slice(0, 160),
  purpose: String(event.purpose || '').trim().slice(0, 240),
  kind: ADVISOR_EVENT_KINDS.includes(event.kind) ? event.kind : 'other',
  date: String(event.date || '').slice(0, 10),
  amount: num(event.amount, 0),
  assetGroup: event.kind === 'investment' && ['a', 'd', 'other'].includes(event.assetGroup) ? event.assetGroup : '',
  assetTaxCost: event.kind === 'investment' && Number(event.assetTaxCost) > 0 ? num(event.assetTaxCost) : null,
  assetBusinessUsePct: event.kind === 'investment' && Number(event.assetBusinessUsePct) > 0
    ? Math.min(100, num(event.assetBusinessUsePct)) : null,
  assetUsefulLifeYears: event.kind === 'investment' && Number(event.assetUsefulLifeYears) > 0
    ? num(event.assetUsefulLifeYears) : null,
  assetAcquiredDate: event.kind === 'investment' ? String(event.assetAcquiredDate || '').slice(0, 10) : '',
  createdAt: event.createdAt || nowIso(),
  updatedAt: nowIso(),
});
const getAdvisorGoals = () => safeArray(mergedRuntimeState().advisorGoals).map(normalizeAdvisorGoal);
const getPlannedCashEvents = () => safeArray(mergedRuntimeState().plannedCashEvents)
  .map(normalizeAdvisorEvent)
  .filter((event) => event.date)
  .sort((a, b) => a.date.localeCompare(b.date));
const saveAdvisorGoal = async (goal) => {
  const next = normalizeAdvisorGoal(goal);
  if (!next.title || !next.deadline) throw new Error(t('svc.advisorGoalIncomplete'));
  await updateStateAndPersist((state) => {
    const list = safeArray(state.advisorGoals);
    const exists = list.some((entry) => entry.id === next.id);
    return {
      ...state,
      advisorGoals: exists ? list.map((entry) => entry.id === next.id ? { ...entry, ...next } : entry) : [...list, next],
    };
  });
  return next;
};
const deleteAdvisorGoal = async (id) => {
  await updateStateAndPersist((state) => ({
    ...state,
    advisorGoals: safeArray(state.advisorGoals).filter((entry) => entry.id !== id),
  }));
};
const savePlannedCashEvent = async (event) => {
  const next = normalizeAdvisorEvent(event);
  if (!next.label || !next.date) throw new Error(t('svc.advisorEventIncomplete'));
  await updateStateAndPersist((state) => {
    const list = safeArray(state.plannedCashEvents);
    const exists = list.some((entry) => entry.id === next.id);
    return {
      ...state,
      plannedCashEvents: exists ? list.map((entry) => entry.id === next.id ? { ...entry, ...next } : entry) : [...list, next],
    };
  });
  return next;
};
const deletePlannedCashEvent = async (id) => {
  await updateStateAndPersist((state) => ({
    ...state,
    plannedCashEvents: safeArray(state.plannedCashEvents).filter((entry) => entry.id !== id),
  }));
};

// ---------------- Status per anbefaling ----------------
// Rent brukereid. Å sette status setter ikke i gang noe — det finnes derfor ingen
// «igangsatt». Fingeravtrykket er tallet rådet gjaldt da brukeren tok stilling, slik
// at et gammelt hakeklikk ikke skjuler et nytt problem på samme konto.
const getAdvisorRecommendationStatuses = () => {
  const stored = mergedRuntimeState().advisorRecommendationStatus;
  return stored && typeof stored === 'object' ? stored : {};
};
// User answers are review prompts for one income year, never booked deductions.
const getTaxDeductionHunter = (year) => {
  return window.PlummeTaxDeductionHunter.year(mergedRuntimeState(), year);
};
const saveTaxDeductionHunterItem = async (year, id, patch = {}) => {
  const yearKey = String(year);
  const itemKey = String(id || '').trim();
  if (!/^\d{4}$/.test(yearKey) || !/^[a-z]+$/.test(itemKey)) throw new Error('Invalid deduction review item.');
  await updateStateAndPersist((state) => window.PlummeTaxDeductionHunter.update(state, yearKey, itemKey, patch, nowIso()));
  return getTaxDeductionHunter(year);
};
const saveTaxDeductionHunterAnswers = async (year, answers = {}) => {
  const yearKey = String(year);
  if (!/^\d{4}$/.test(yearKey)) throw new Error('Invalid deduction review year.');
  const allowed = new Set(['operations', 'sales', 'travel', 'assets', 'finance', 'people']);
  const entries = Object.entries(answers).filter(([id, answer]) => allowed.has(id) && (answer === 'yes' || answer === 'no'));
  if (!entries.length) return getTaxDeductionHunter(year);
  await updateStateAndPersist((state) => window.PlummeTaxDeductionHunter.updateAnswers(state, yearKey, Object.fromEntries(entries), nowIso()));
  return getTaxDeductionHunter(year);
};
const setAdvisorRecommendationStatus = async (id, patch = {}) => {
  const key = String(id || '').trim();
  if (!key) throw new Error(t('svc.advisorStatusMissingId'));
  const allowed = globalThis.PlummeAiAdvisorStatus?.STATUSES || ['new', 'snoozed', 'done', 'dismissed'];
  const status = allowed.includes(patch.status) ? patch.status : 'new';
  const entry = stripUndefinedDeep({
    status,
    note: patch.note ? String(patch.note).slice(0, 200) : undefined,
    snoozeUntil: status === 'snoozed' && patch.snoozeUntil ? String(patch.snoozeUntil).slice(0, 10) : undefined,
    fingerprint: patch.fingerprint ? String(patch.fingerprint).slice(0, 200) : undefined,
    at: nowIso(),
  });
  // Merkes rådet som gjort, fryser vi måletallet fra regnskapet akkurat nå. Uten det
  // har vi ingenting å måle utfallet mot senere — «det ble bedre» krever et «før».
  const outcome = status === 'done' ? buildAdvisorOutcomeEntry(key, patch) : null;
  await updateStateAndPersist((state) => {
    const current = state.advisorRecommendationStatus && typeof state.advisorRecommendationStatus === 'object'
      ? state.advisorRecommendationStatus
      : {};
    // «Ny» er fravær av status, ikke en lagret verdi — ellers vokser kartet med
    // rader som ikke betyr noe.
    const nextStatuses = { ...current };
    if (status === 'new') delete nextStatuses[key];
    else nextStatuses[key] = entry;
    return {
      ...state,
      advisorRecommendationStatus: nextStatuses,
      advisorOutcomes: outcome
        ? [...safeArray(state.advisorOutcomes).filter((item) => item.id !== outcome.id), outcome].slice(-100)
        : safeArray(state.advisorOutcomes),
    };
  });
  return entry;
};
// Måletallet hentes fra hjem-modellen, ikke fra anbefalingsteksten: teksten er
// formatert for lesing, tallet skal kunne trekkes fra senere.
const advisorOutcomeMetrics = () => {
  const home = getHomeDashboardModel();
  return {
    salesOverdue: num(home?.overview?.salesOverdue, 0),
    missingAttachmentCount: num(home?.followup?.missingAttachmentCount, 0),
    vatDue: num(home?.publicFees?.vatDue, 0),
  };
};
const buildAdvisorOutcomeEntry = (recId, patch = {}) => {
  const metric = globalThis.PlummeAiAdvisorOutcomes?.METRIC_BY_REC?.[recId];
  const metrics = advisorOutcomeMetrics();
  return stripUndefinedDeep({
    // Én oppføring per anbefaling per merking — merker du på nytt, erstattes den
    // forrige i stedet for at historikken fylles med duplikater.
    id: `${recId}:${nowIso().slice(0, 10)}`,
    recId,
    effectType: patch.effectType || undefined,
    estimateLabel: patch.estimateLabel || undefined,
    metric: metric || undefined,
    metricValue: metric ? metrics[metric] : undefined,
    markedAt: nowIso(),
  });
};
const getAiAdvisorOutcomesModel = () => cachedDerivedModel('advisorOutcomes', () => {
  if (!globalThis.PlummeAiAdvisorOutcomes) return null;
  return globalThis.PlummeAiAdvisorOutcomes.buildOutcomesModel({
    entries: safeArray(mergedRuntimeState().advisorOutcomes),
    metricsNow: advisorOutcomeMetrics(),
  });
});

// Budsjett mot faktisk for rådgiveren. Gjenbruker budsjettmodellen i sin helhet —
// det er samme budsjett brukeren redigerer på Budsjett-siden, ikke en egen kopi.
const getAiAdvisorPlanModel = (options = {}) => {
  if (!globalThis.PlummeAiAdvisorPlan) return null;
  const budgetModel = getBudgetModel({ year: options.year });
  if (!budgetModel) return null;
  const advisor = getAiAdvisorModel({ year: budgetModel.year });
  const report = deriveReportResult(mergedRuntimeState(), budgetModel.year);
  // Nåverdien for hvert mål hentes fra bokførte tall, ikke fra noe brukeren har skrevet
  // inn. Brukeren eier målet; Plumme eier målingen.
  const metricNow = {
    buffer: advisor?.forecast?.length ? num(advisor.forecast[advisor.forecast.length - 1].balance) : 0,
    income: num(report.inntekter),
    result: num(report.resultat),
    margin: num(report.inntekter) > 0 ? (num(report.resultat) / num(report.inntekter)) * 100 : 0,
  };
  return globalThis.PlummeAiAdvisorPlan.buildBudgetVsActual({
    year: budgetModel.year,
    budgetLines: budgetModel.budgetLines,
    actualLines: budgetModel.actualLines,
    goals: getAdvisorGoals(),
    events: getPlannedCashEvents(),
    metricNow,
  });
};
const getAiAdvisorModel = (options = {}) => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergedRuntimeState();
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  return cachedDerivedModel(`aiAdvisor:${year}`, () => buildAiAdvisorModelForYear(state, year));
};
const buildAiAdvisorModelForYear = (state, year) => {
  const home = getHomeDashboardModel();
  if (!home) return null;
  const report = addImportedSalesHistoryToAdvisorReport(deriveReportResult(state, year), state, year);
  const previousReport = addImportedSalesHistoryToAdvisorReport(deriveReportResult(state, year - 1), state, year - 1);
  const previousYearAvailable = aiAdvisorPreviousYearAvailable(state, year, previousReport);
  const income = num(report.inntekter);
  const partyInsights = aiAdvisorImportedPartyInsights(state, year, report);
  const importedCustomerShare = partyInsights.customers[0]?.shareBasis === 'yearIncome' ? partyInsights.customers[0].sharePct : null;
  const customerConcentration = state.accountingImportSalesHistorySummary?.years?.[String(year)]?.documentCount
    ? buildAiAdvisorSalesModelForYear(state, year)?.kpis?.topCustomerSharePct ?? null
    : importedCustomerShare ?? aiAdvisorCustomerConcentration(state, year, income);
  return window.PlummeAiAdvisorModel?.buildAiAdvisorModel?.({
    year,
    home,
    report,
    previousReport,
    previousYearAvailable,
    cashBasis: window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state) ? null : window.PlummeAdvisorCashBasis.build({ state, year, readTrial: (month) => deriveTrialBalance(state, year, { months: [month, month] }) }),
    settings: state.settings,
    customerConcentration,
    partyInsights,
    state,
  }) || null;
};
const saveAiAdvisorLiquidityBuffer = async (value = {}) => {
  const requestedMode = value?.mode === 'custom' ? 'custom' : 'automatic';
  const amount = Math.round(num(value?.amount));
  if (requestedMode === 'custom' && amount <= 0) {
    throw new Error(t('adv.buffer.invalid'));
  }
  const setting = requestedMode === 'custom'
    ? { mode: 'custom', amount }
    : { mode: 'automatic' };
  await updateStateAndPersist((state) => ({
    ...state,
    settings: {
      ...(state.settings || {}),
      aiAdvisorLiquidityBuffer: setting,
    },
  }));
  return setting;
};
// Rådgiverprofilen er brukerens egne preferanser for hvordan råd skal gis. Den styrer
// ingen regnskapslogikk — den er kontekst til rådgiveren, og lagres derfor sammen med
// øvrige innstillinger og ikke som en egen regnskapsentitet.
// Where each field is used: mainGoal/risk/accountantInvolved re-rank and label tasks via
// PlummeAiAdvisorTasks.applyAdvisorProfile and go into the AI prompts
// (functions/aiAdvisor/advisorPreferences.ts); cadence sets how often the daily insight
// is generated automatically. The option lists and defaults must match that file.
// Cadence defaults to daily because that is what the daily insight did before the
// setting had any effect.
const AI_ADVISOR_PROFILE_DEFAULTS = { mainGoal: 'buffer', risk: 'balanced', cadence: 'daily', accountantInvolved: false };
const AI_ADVISOR_PROFILE_OPTIONS = {
  mainGoal: ['revenue', 'profitability', 'buffer', 'hiring', 'investment', 'workload', 'dividend'],
  risk: ['cautious', 'balanced', 'offensive'],
  cadence: ['daily', 'weekly', 'monthly'],
};
// Utbytte forutsetter et selskap som kan dele ut utbytte. Et enkeltpersonforetak tar
// privat uttak, ikke utbytte — maalet ville ikke betydd noe der.
const AI_ADVISOR_DIVIDEND_TYPES = ['AS', 'ASA', 'NUF'];
const aiAdvisorGoalAvailable = (goal, settings = {}) => goal !== 'dividend' ||
  AI_ADVISOR_DIVIDEND_TYPES.includes(String(settings.companyTypeCode || '').toUpperCase());
// Valgene UI skal tegne. Backend eier listen, saa siden ikke kan tilby et maal som
// ikke kan lagres.
const getAiAdvisorProfileOptions = () => {
  const settings = mergeState(runtime.appState).settings || {};
  return {
    mainGoal: AI_ADVISOR_PROFILE_OPTIONS.mainGoal.filter((goal) => aiAdvisorGoalAvailable(goal, settings)),
    risk: [...AI_ADVISOR_PROFILE_OPTIONS.risk],
    cadence: [...AI_ADVISOR_PROFILE_OPTIONS.cadence],
  };
};
// Foretaksdata fra registeret, slik det ble lagret ved verifisert claim. Brukes til
// bransjesammenligning; feltene er tomme for foretak som ble opprettet manuelt eller
// i land uten registeroppslag.
const getActiveBusinessProfile = () => {
  const active = safeArray(runtime.memberships).find((membership) => membership.businessId === runtime.activeBusinessId) || null;
  if (!active) return {};
  return {
    businessId: active.businessId,
    country: active.country || '',
    companyTypeCode: active.companyTypeCode || '',
    companyTypeLabel: active.companyTypeLabel || '',
    industryCode: active.industryCode || '',
    industryLabel: active.industryLabel || '',
  };
};
// Satsene «Hva skjer hvis …» bruker som standard for et ansettelsesscenario. Vi leser
// foretakets egne lønnsinnstillinger i stedet for å hardkode en sats som ville vært
// feil for andre soner og land.
const getAdvisorPayrollRates = () => {
  const settings = mergeState(runtime.appState).payrollSettings || {};
  return {
    employerContributionRate: num(settings.employerContributionRate, 14.1),
    holidayPayPercent: num(settings.holidayPayPercent, 10.2),
  };
};
// Fornavn til hilsenen i rådgiverens morgenbrief. Returnerer null når vi ikke vet
// navnet — da hilser briefen uten navn i stedet for å gjette.
const getAdvisorGreetingName = () => {
  const settings = mergeState(runtime.appState).settings || {};
  const name = settings.contactName || settings.contactPerson || runtime.userProfile?.displayName || runtime.user?.displayName || '';
  return String(name).trim().split(/\s+/)[0] || null;
};
const getAiAdvisorProfile = () => {
  const saved = mergeState(runtime.appState).settings?.aiAdvisorProfile || {};
  return {
    mainGoal: AI_ADVISOR_PROFILE_OPTIONS.mainGoal.includes(saved.mainGoal) ? saved.mainGoal : AI_ADVISOR_PROFILE_DEFAULTS.mainGoal,
    risk: AI_ADVISOR_PROFILE_OPTIONS.risk.includes(saved.risk) ? saved.risk : AI_ADVISOR_PROFILE_DEFAULTS.risk,
    // Profiles saved before 2026-09-27 stored cadence 'weekly' without the user choosing it,
    // so a cadence only counts once the user has actively picked one (cadenceChosen).
    cadence: saved.cadenceChosen === true && AI_ADVISOR_PROFILE_OPTIONS.cadence.includes(saved.cadence) ? saved.cadence : AI_ADVISOR_PROFILE_DEFAULTS.cadence,
    cadenceChosen: saved.cadenceChosen === true,
    accountantInvolved: saved.accountantInvolved === true,
  };
};
const saveAiAdvisorProfile = async (patch = {}) => {
  const next = { ...getAiAdvisorProfile() };
  Object.entries(patch).forEach(([key, value]) => {
    if (key === 'accountantInvolved') next.accountantInvolved = value === true;
    else if (AI_ADVISOR_PROFILE_OPTIONS[key]?.includes(value)) next[key] = value;
  });
  if (Object.prototype.hasOwnProperty.call(patch, 'cadence') && AI_ADVISOR_PROFILE_OPTIONS.cadence.includes(patch.cadence)) next.cadenceChosen = true;
  await updateStateAndPersist((state) => ({
    ...state,
    settings: { ...(state.settings || {}), aiAdvisorProfile: next },
  }));
  return next;
};
// Avvisningsloggen er ikke en egen lagring: den leses ut av statusene brukeren allerede
// har satt på anbefalingene, så «hva jeg har lært» aldri kan vise noe annet enn det
// som faktisk står på kortene.
const getAiAdvisorDismissLog = () => {
  const statuses = getAdvisorRecommendationStatuses() || {};
  return Object.entries(statuses)
    .filter(([, value]) => value?.status === 'dismissed' && value.note)
    .map(([recId, value]) => ({ recId, reason: value.note, at: value.updatedAt || null }))
    .slice(0, 10);
};
// Bransjeindeksen bygges av en planlagt jobb og leses gjennom backend, ikke direkte
// fra Firestore. Returnerer null når gruppen ikke er bygget ennå — da sier fanen det.
const fetchIndustryIndex = async ({ industryCode, sizeBand } = {}) => {
  if (!industryCode || !sizeBand) return null;
  const ok = await initFirebase();
  if (!ok || !runtime.auth?.currentUser) return null;
  try {
    const payload = await authorizedPost('getIndustryIndexHttp', { industryCode, sizeBand });
    return payload.index || null;
  } catch {
    // Uten indeks viser fanen sammenligningsgrunnlaget og sier at tallene mangler.
    return null;
  }
};
const fetchAiAdvisorModel = async (options = {}) => {
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebase'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAdvisor'));
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const payload = await authorizedPost('getAiAdvisorModelHttp', {
    year,
    businessId: options.businessId || runtime.activeBusinessId || undefined,
  });
  return payload.model || null;
};
const fetchAiAdvisorInsights = async (options = {}) => {
  const ok = await initFirebase();
  if (!ok) throw new Error(t('svc.noFirebase'));
  if (!runtime.auth?.currentUser) throw new Error(t('svc.needLoginAdvisor'));
  const state = mergeState(runtime.appState);
  const year = Number(options.year || state.activeYear) || new Date().getFullYear();
  const locale = options.locale || uiLocale();
  const payload = await authorizedPost('getAiAdvisorInsightHttp', {
    year,
    locale,
    businessId: options.businessId || runtime.activeBusinessId || undefined,
    forceDaily: options.forceDaily === true,
    forceMonthly: options.forceMonthly === true,
    forcePreviousMonthly: options.forcePreviousMonthly === true,
  });
  return payload.insights || null;
};

// Skattemeldingen og aarsregnskapet utledet hver sin kopi av den samme
// arsrapporten. `deriveReports` gaar gjennom alle bilag, saa det var to fulle
// gjennomganger der en holder. Noekkelen er den samme som `getReportsModel`
// bruker for et helt ar uten filtre.
